/*
THESIS: Turn a long editing backlog into one clear review path; reject the repeated feature-section SaaS page.
OWN-WORLD: A continuous white canvas, near-black type, cool gray rules and one cobalt progress signal.
STORY: See the promise, understand the three-step path, recognize which busywork disappears, then start a project.
FIRST VIEWPORT: A centered two-line promise and primary action lead directly into the connected review runway.
FORM: A minimalist creator run sheet, ranked direction 6 and resolved as “The Review Runway”; seed 7b7d9ef7.
*/
import { useEffect, useRef, useState } from "react"
import MuxPlayer from "@mux/mux-player-react/lazy"
import { Badge } from "@/components/ui/badge"
import { LandingSourceForm } from "@/components/landing-source-form"
import { PublicFaq } from "@/components/public-faq"
import { PublicNavbar } from "@/components/public-navbar"
import { SavingHoursSection } from "@/components/saving-hours-section"

import { createFileRoute } from "@tanstack/react-router"
import { landingLinks, landingMeta, landingStructuredData } from "@/lib/seo"

export const Route = createFileRoute("/")({
  head: () => {
    const structuredData = landingStructuredData()
    return {
      meta: landingMeta,
      links: landingLinks,
      scripts: structuredData
        ? [
            {
              type: "application/ld+json",
              children: JSON.stringify(structuredData),
            },
          ]
        : [],
    }
  },
  component: LandingPage,
})

const benefits = [
  {
    title: "More content from what you already create.",
    lead: "More content from",
    accent: "what you already create.",
    copy: "Repurpose long-form video into shorts from the recordings and streams you already have.",
    owner: "Make more of every recording",
    demo: "One source, several shorts",
    playbackId: "fQlQuc4B6dOX01Un4w00EyTHzahR2Wzsllcx017QMX64O00",
  },
  {
    title: "Less searching. More worth reviewing.",
    lead: "Less searching.",
    accent: "More worth reviewing.",
    copy: "An AI video clipper selects moments from your recording so you can review the cuts and context.",
    owner: "AI video clipping",
    demo: "A moment worth reviewing",
    playbackId: "5C31eBqJEA7z4rShdKT8KXgWzl9U00xD9GRiokkD5201s",
  },
  {
    title: "Start with captioned shorts.",
    lead: "Start with",
    accent: "captioned shorts.",
    copy: "Get automatic captions with your first cuts, then correct the text and adjust framing and style.",
    owner: "Skip the repetitive first pass",
    demo: "From source to captioned draft",
    playbackId: "DkmvvaiGU00mcu00bwzEMy02klGFBK3n3Ywsl01icmks6Jc",
  },
  {
    title: "Keep your voice. Have the final say.",
    lead: "Keep your voice.",
    accent: "Have the final say.",
    copy: "Refine each suggestion and choose which shorts you export.",
    owner: "Keep creative control",
    demo: "Your edit, your final short",
    playbackId: "xMcC00oSzfI4sKUG4GTuYlnYL9fYbgyAab300Fut4SNy4",
  },
]

const workflow = [
  {
    title: "Paste your video link",
    copy: "Paste a YouTube or public Twitch VOD link, or upload a video from your computer.",
    playbackId: "7uMZh67r1FzdTdXefH00WtV7WUX9gKNlOiCjITxmPdCc",
    demo: "Pasting a source video link into intoshort",
  },
  {
    title: "Choose your settings",
    copy: "Choose the moments to look for, clip length, captions and video quality in the configuration dialog.",
    playbackId: "C42J3zqX00fPK2Imu4W9XTET01joC00iWy96jcm4j02VRso",
    demo: "Configuring shorts in the video settings dialog",
  },
  {
    title: "Review your shorts",
    copy: "Once processing is complete, review the suggestions, refine the cuts and captions, then export the shorts you choose.",
    playbackId: "NloREnvZJmyd01IJp01Glnj9KiASihEUIoVq7JAmflsuU",
    demo: "Reviewing and exporting the suggested shorts",
  },
]


function LandingPage() {
  const headerRef = useRef<HTMLElement>(null)
  const framingSectionRef = useRef<HTMLElement>(null)
  const editorSectionRef = useRef<HTMLElement>(null)
  const [editorTheme, setEditorTheme] = useState(false)

  useEffect(() => {
    let frame: number | null = null

    const updateEditorTheme = () => {
      frame = null
      const header = headerRef.current
      const sections = [
        framingSectionRef.current,
        editorSectionRef.current,
      ].filter((section): section is HTMLElement => section !== null)
      if (!header || sections.length === 0) return
      const headerRect = header.getBoundingClientRect()
      const isOverlapping = sections.some((section) => {
        const sectionRect = section.getBoundingClientRect()
        return (
          sectionRect.top <= headerRect.bottom &&
          sectionRect.bottom >= headerRect.top
        )
      })
      setEditorTheme((current) =>
        current === isOverlapping ? current : isOverlapping
      )
    }

    const scheduleUpdate = () => {
      if (frame !== null) return
      frame = window.requestAnimationFrame(updateEditorTheme)
    }

    updateEditorTheme()
    window.addEventListener("scroll", scheduleUpdate, { passive: true })
    window.addEventListener("resize", scheduleUpdate)
    const resizeObserver = new ResizeObserver(scheduleUpdate)
    if (headerRef.current) resizeObserver.observe(headerRef.current)
    if (framingSectionRef.current) {
      resizeObserver.observe(framingSectionRef.current)
    }
    if (editorSectionRef.current) resizeObserver.observe(editorSectionRef.current)

    return () => {
      window.removeEventListener("scroll", scheduleUpdate)
      window.removeEventListener("resize", scheduleUpdate)
      resizeObserver.disconnect()
      if (frame !== null) window.cancelAnimationFrame(frame)
    }
  }, [])

  return (
    <div className="landing landing-home forge-landing" id="top">
      <a className="skip-link" href="#contenu">
        Skip to content
      </a>
      <PublicNavbar headerRef={headerRef} editorTheme={editorTheme} />

      <main id="contenu" className="forge-main">
        <section className="forge-hero page-width" aria-labelledby="hero-title">
          <div className="forge-hero-copy">
            <Badge variant="outline" className="forge-badge">
              AI clipping. Your final say.
            </Badge>
            <h1 id="hero-title">
              <span className="forge-title-main">Turn long videos</span>
              <span className="forge-title-sub">into <span className="forge-heading-accent">shorts.</span></span>
            </h1>
            <p className="forge-hero-lead">
              intoshort turns YouTube videos, Twitch VODs and uploads into
              editable vertical shorts with captions. AI finds the moments and
              makes the first cut. You decide what gets published.
            </p>
            <LandingSourceForm />
          </div>
        </section>

        <section
          ref={framingSectionRef}
          className="forge-framing"
          aria-labelledby="framing-title"
        >
          <div className="page-width">
            <div className="forge-section-intro">
              <p>Automatic reframing</p>
              <h2 id="framing-title">From widescreen to <span className="forge-heading-accent">vertical.</span></h2>
              <span>
                Keep the facecam and gameplay in view, with captions between them.
              </span>
            </div>
            <MuxPlayer
              className="forge-demo-video forge-framing-video rounded-md"
              style={{ aspectRatio: "226 / 135" }}
              loading="viewport"
              preload="none"
              playbackId="dGiKBjkGFFcsIX86RCsKguYKHh02XvQaEcPTjM6Hk02oc"
              aria-label="Widescreen to vertical reframing demo"
              autoPlay="muted"
              muted
              loop
              playsInline
              nohotkeys
            />
          </div>
        </section>

        <section
          id="benefits"
          className="forge-how page-width"
          aria-label="What you gain with intoshort"
        >
          <ul className="forge-runway">
            {benefits.map((benefit, index) => (
              <li key={benefit.title}>
                <div className="forge-step-copy">
                  <div className="forge-step-head">
                    <p className="forge-step-owner">{benefit.owner}</p>
                  </div>
                  <h2>
                    <span className={`forge-heading-slot forge-heading-accent-${index % 3}`}>
                      {benefit.lead}{" "}
                      <span className="forge-heading-accent">{benefit.accent}</span>
                    </span>
                  </h2>
                  <p>{benefit.copy}</p>
                </div>
                <div className="forge-benefit-placeholder flex aspect-[226/135] min-w-0 items-center rounded-lg bg-muted p-4 sm:p-8">
                  <MuxPlayer
                    className="forge-demo-video rounded-md"
                    style={{ aspectRatio: "226 / 135" }}
                    loading="viewport"
                    preload="none"
                    playbackId={benefit.playbackId}
                    aria-label={benefit.demo}
                    autoPlay="muted"
                    muted
                    loop
                    playsInline
                    nohotkeys
                  />
                </div>
              </li>
            ))}
          </ul>
        </section>

        <section
          id="how-it-works"
          className="forge-how forge-workflow page-width"
          aria-labelledby="how-it-works-title"
        >
          <h2 id="how-it-works-title">How it works</h2>
          <ol className="forge-workflow-steps">
            {workflow.map((step) => (
              <li key={step.playbackId}>
                <MuxPlayer
                  className="forge-demo-video rounded-md"
                  style={{ aspectRatio: "226 / 135" }}
                  loading="viewport"
                  preload="none"
                  playbackId={step.playbackId}
                  aria-label={step.demo}
                  autoPlay="muted"
                  muted
                  loop
                  playsInline
                  nohotkeys
                />
                <h3>{step.title}</h3>
                <p>{step.copy}</p>
              </li>
            ))}
          </ol>
        </section>

        <SavingHoursSection />

        <section
          ref={editorSectionRef}
          className="forge-editor-showcase"
          aria-labelledby="editor-title"
        >
          <div className="page-width">
            <div className="forge-editor-showcase-copy">
              <p>Built-in editor</p>
              <h2 id="editor-title">Shape <span className="forge-heading-accent">every short</span> before it leaves your hands.</h2>
              <span>
                Refine the cut, arrange media, and tune captions in one timeline
                before you export.
              </span>
            </div>
            <MuxPlayer
              className="forge-demo-video forge-editor-showcase-video rounded-md"
              style={{ aspectRatio: "226 / 135" }}
              loading="viewport"
              preload="none"
              playbackId={
                new URL(
                  "https://player.mux.com/7Ep1CjpRxRDCx5W1gy00Lw7goJiANrB01Kz1hCIdRyzNU?metadata-video-title=editor-showcase&video-title=editor-showcase"
                ).pathname.slice(1)
              }
              aria-label="A preview of the built-in short editor"
              autoPlay="muted"
              muted
              loop
              playsInline
              nohotkeys
            />
          </div>
        </section>

        <PublicFaq />

      </main>
    </div>
  )
}
